import {
  Avatar,
  Box,
  Button,
  Fab,
  Grid,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import useAI from "src/hooks/useAI";
import { setAIState } from "src/slices/ai";
import { useDispatch, useSelector } from "src/store";

const AIChatBot = ({ role }) => {
  const { t } = useTranslation();
  const { messages, iaOpen, loadingIA } = useSelector((state) => state.ai);
  const dispatch = useDispatch();
  const { adminMode } = useSelector((state) => state.settings);
  const { chatMessage, clearChat, generateContent, setInitialMessage } =
    useAI();
  const initialized = React.useRef(false);
  const { currentCustomer } = useSelector((state) => state.customers);
  const [viewState, setViewState] = React.useState({
    message: "",
    messages: [],
    loading: false,
    error: null,
    useIA: false,
    open: false,
  });

  const handleSendMessage = async () => {
    if (viewState.message === "") {
      return;
    }
    setViewState({ ...viewState, loading: true });
    if (role !== "chat") {
      await generateContent(viewState.message, role);
    } else {
      await chatMessage(viewState.message, role);
    }
    setViewState({ ...viewState, loading: false, message: "" });
  };

  React.useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      // if (role === "redactor") {
      //   setInitialMessage(
      //     `
      //     Hola soy un redactor de IA, escribe tu idea y te ayudaré a escribir un
      //     texto lenguaje profesional y técnico, de manera rápida y eficiente\n\n
      //     Revisa detalladamente el texto antes de publicarlo, ya que la IA
      //     no garantiza la exactitud de las respuestas\n\n
      //     `
      //   );
      // }
    }
  }, [role]);

  if (!adminMode) {
    return null;
  }
  if (!iaOpen) {
    // show chat button to open the chat
    return (
      <Zoom in={!iaOpen} unmountOnExit>
        <Fab
          variant="extended"
          color="primary"
          onClick={() => dispatch(setAIState({ iaOpen: true }))}
        >
          {/* <ChatBubble sx={{ mr: 1 }} /> */}
          <Avatar
            src={
              currentCustomer?.apps_config?.chatBot?.icon ||
              "/static/icons/chat_bot_icon.webp"
            }
            alt="icon"
            style={{
              height: 32,
              width: 32,
              marginRight: 8,
            }}
          />
          {currentCustomer?.apps_config?.chatBot?.buttonText || t("Chat")}
        </Fab>
      </Zoom>
    );
  }
  // we need render chat message and input box elegant, very well designed
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 400,
        maxWidth: "90vw",
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 2,
        borderRadius: 2,
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Grid
          container
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Grid item sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={
                currentCustomer?.apps_config?.chatBot?.icon ||
                "/static/icons/chat_bot_icon.webp"
              }
              alt="icon"
              style={{
                height: 64,
                marginRight: 8,
              }}
            />
            <Typography variant="h6">
              {currentCustomer?.apps_config?.chatBot?.title ||
                t("Gestión Ciudad IA")}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="text"
              color="secondary"
              onClick={() => dispatch(setAIState({ iaOpen: false }))}
            >
              {t("Cerrar")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loadingIA && <Typography variant="body2">{t("Cargando...")}</Typography>}
      {messages.length === 0 && !loadingIA && (
        <ChatMessageWidget
          m={{
            from: "ia",
            message:
              currentCustomer?.apps_config?.chatBot?.emptyStateText ||
              t("Hola, ¿en qué puedo ayudarte?"),
          }}
        />
      )}
      {role === "chat" && (
        <ChatView
          messages={messages}
          viewState={viewState}
          setViewState={setViewState}
          handleSendMessage={handleSendMessage}
        />
      )}
      {role === "redactor" && (
        <RedactorView
          messages={messages}
          viewState={viewState}
          setViewState={setViewState}
          handleSendMessage={handleSendMessage}
        />
      )}
    </Box>
  );
};
export default AIChatBot;

const ChatView = ({ messages, viewState, setViewState, handleSendMessage }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: "100%" }}>
      {/* Messages container */}
      <Box
        sx={{
          height: "400px",
          overflowY: "auto",
          padding: 2,
          marginBottom: 2,
          display: "flex",
          flexDirection: "column-reverse",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {messages.map((m, i) => (
          <ChatMessageWidget key={i} m={m} />
        ))}
      </Box>

      {/* Input area */}
      <Box
        component="form"
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          backgroundColor: "background.paper",
          padding: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
      >
        <TextField
          fullWidth
          placeholder={t("Escribe un mensaje...")}
          multiline
          maxRows={4}
          value={viewState.message}
          onChange={(e) =>
            setViewState({ ...viewState, message: e.target.value })
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          disabled={!viewState.message.trim()}
          sx={{
            height: 56,
            width: 100,
            borderRadius: 2,
          }}
        >
          {t("Enviar")}
        </Button>
      </Box>
    </Box>
  );
};

const RedactorView = ({
  messages,
  viewState,
  setViewState,
  handleSendMessage,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          height: "400px",
          overflowY: "auto",
          padding: 2,
          marginBottom: 2,
          display: "flex",
          flexDirection: "column-reverse",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {messages.map((m, i) => (
          <ChatMessageWidget key={i} m={m} />
        ))}
      </Box>

      <Box
        component="form"
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          backgroundColor: "background.paper",
          padding: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
      >
        <TextField
          fullWidth
          placeholder={t("Escribe tu texto para redactar...")}
          multiline
          rows={4}
          value={viewState.message}
          onChange={(e) =>
            setViewState({ ...viewState, message: e.target.value })
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          disabled={!viewState.message.trim()}
          sx={{
            height: 56,
            width: 100,
            borderRadius: 2,
          }}
        >
          {t("Redactar")}
        </Button>
      </Box>
    </Box>
  );
};

const ChatMessageWidget = ({ m }) => {
  const [displayedMessage, setDisplayedMessage] = React.useState("");
  const messageRef = React.useRef(null);
  const [userScrolled, setUserScrolled] = React.useState(false);

  React.useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index < m.message.length) {
        var mI = m.message[index];
        if (mI === "\n") mI = "\n\n"; // add extra line break for markdown
        if (mI === "undefined") mI = "";
        setDisplayedMessage((prev) => prev + mI);
        index++;
        // Scroll to bottom smoothly only if user hasn't scrolled
        if (messageRef.current && !userScrolled) {
          // scroll to bottom
          messageRef.current.scrollTo({
            top: messageRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      } else {
        clearInterval(timer);
      }
    }, 30);

    const handleScroll = () => {
      setUserScrolled(true);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      clearInterval(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [m.message, userScrolled]);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }} ref={messageRef}>
      {m.from === "user" && <Grid item xs={2} />}
      <Grid item xs={10}>
        <Box
          sx={{
            backgroundColor: m.from === "user" ? "primary.main" : "grey.100",
            borderRadius: 2,
            p: 2,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 10,
              [m.from === "user" ? "right" : "left"]: -10,
              border: "10px solid transparent",
              borderRightColor: m.from === "user" ? "transparent" : "grey.100",
              borderLeftColor:
                m.from === "user" ? "primary.main" : "transparent",
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color:
                m.from === "user" ? "primary.contrastText" : "text.primary",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            <Markdown>
              {m.from === "user" ? m.message : displayedMessage}
            </Markdown>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color:
                m.from === "user" ? "primary.contrastText" : "text.secondary",
              opacity: 0.7,
              display: "block",
              textAlign: m.from === "user" ? "right" : "left",
              mt: 1,
            }}
          >
            {new Date(m.timestamp || Date.now()).toLocaleTimeString()}
          </Typography>
        </Box>
      </Grid>
      {m.from === "ia" && <Grid item xs={2} />}
    </Grid>
  );
};
